import { PostsRoot } from '@/composables/post/postList'
import { Ref, ref } from 'vue'
import useAppState from '@/composables/ui/ui'
import useCurrentUser from '@/composables/user/user'
import { SettingsStatus } from '@/model/data-object/Post'
import { TOOLTIP_COMPONENTS } from '@/composables/post/postTooltip'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_EDIT_OPEN,
} from '@/composables/analytics/analyticsManager'

const MAX_EDIT_AGE = 300
export const MAX_EDIT_OPENED_AGE = 300

function resolveIsMine(postRoot: PostsRoot): boolean {
  const { currentUser } = useCurrentUser()

  return (
    typeof currentUser.value !== 'undefined' &&
    currentUser.value?.id === postRoot.post?.author.id
  )
}

function isNotActive(postRoot: PostsRoot): boolean {
  return postRoot.post?.status !== SettingsStatus.ACTIVE
}

export default function usePostEdit(postRoot: PostsRoot, emit: any) {
  const { initTime, refreshInitTime } = useAppState()
  const { propagateAnalyticsEvent } = useAnalyticsManager()
  refreshInitTime()

  let postAge = 0
  let canEditUntil = 0
  const canEdit = ref<boolean>(false)
  const isMine = resolveIsMine(postRoot)

  const toggleEdit = () => {
    emit('toggle-edit-form')
    if (postRoot.post) {
      propagateAnalyticsEvent(postRoot.post, ANALYTICS_EVENT_POST_EDIT_OPEN)
    }
  }

  if (isNotActive(postRoot)) {
    return {
      canEditUntil,
      postAge,
      canEdit,
      isMine,
    }
  }

  if (postRoot.post) {
    postAge = Math.round(
      (initTime.value.getTime() - postRoot.post.createdAt.getTime()) / 1000
    )
    postAge = postAge < 0 ? 0 : postAge
  }

  canEdit.value = postAge < MAX_EDIT_AGE
  if (canEdit.value) {
    canEditUntil = MAX_EDIT_AGE - postAge
  }

  function disableEdit() {
    canEdit.value = false
  }

  return {
    canEditUntil,
    postAge,
    canEdit,
    isMine,
    toggleEdit,
    disableEdit,
  }
}

export function usePostEditTooltip(
  currentComponent: Ref<TOOLTIP_COMPONENTS | undefined>,
  postEditing: Ref<boolean>
) {
  const { currentUser } = useCurrentUser()

  const toggleTooltip = () => {
    if (currentComponent.value === TOOLTIP_COMPONENTS.POST_EDIT) {
      currentComponent.value = undefined

      return
    }
    if (currentUser.value?.isSubscriptionPremium) {
      postEditing.value = !postEditing.value

      return
    }
    currentComponent.value = TOOLTIP_COMPONENTS.POST_EDIT
  }

  return {
    toggleTooltip,
  }
}
